import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Truck, FileText, Clock, ArrowRight, Building2 } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function CompanyDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ vehicles: 0, bookings: 0, pending_bookings: 0 });
  const [company, setCompany] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/bookings`, { headers, withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data.slice(0, 5));

      // Fetch company if exists
      if (user?.company_id) {
        const companyRes = await axios.get(`${API}/companies/${user.company_id}`);
        setCompany(companyRes.data);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const hasCompany = !!company;

  return (
    <DashboardLayout title="Dashboard Azienda">
      <div className="space-y-6" data-testid="company-dashboard">
        {/* Welcome / Setup */}
        {!hasCompany ? (
          <div className="bg-primary text-primary-foreground p-6">
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Completa la registrazione della tua azienda
            </h2>
            <p className="text-primary-foreground/80 mb-4">
              Per iniziare a pubblicare i tuoi veicoli, devi prima registrare la tua azienda.
            </p>
            <Button
              onClick={() => navigate("/company/profilo")}
              className="bg-white text-primary hover:bg-white/90"
              data-testid="setup-company-btn"
            >
              Registra Azienda
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="bg-foreground text-background p-6">
            <div className="flex items-center gap-4 mb-2">
              <Building2 className="h-8 w-8 text-primary" />
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {company.name}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {company.city}, {company.province}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        {hasCompany && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  I Miei Veicoli
                </CardTitle>
                <Truck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">{stats.vehicles}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Prenotazioni Totali
                </CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono">{stats.bookings}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  In Attesa
                </CardTitle>
                <Clock className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono text-primary">{stats.pending_bookings}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Actions */}
        {hasCompany && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold mb-1">Gestisci Veicoli</h3>
                    <p className="text-sm text-muted-foreground">
                      Aggiungi, modifica o rimuovi veicoli dalla tua flotta
                    </p>
                  </div>
                  <Button onClick={() => navigate("/company/veicoli")} className="btn-primary py-2" data-testid="manage-vehicles">
                    Veicoli
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold mb-1">Prenotazioni</h3>
                    <p className="text-sm text-muted-foreground">
                      Visualizza e gestisci le prenotazioni ricevute
                    </p>
                  </div>
                  <Button onClick={() => navigate("/company/prenotazioni")} variant="outline" data-testid="manage-bookings">
                    Prenotazioni
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Bookings */}
        {hasCompany && recentBookings.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                Prenotazioni Recenti
              </CardTitle>
              <Button variant="ghost" onClick={() => navigate("/company/prenotazioni")}>
                Vedi Tutte
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentBookings.map((booking) => (
                  <div
                    key={booking.booking_id}
                    className="flex items-center justify-between p-4 bg-muted/50 border border-border"
                  >
                    <div className="flex items-center gap-4">
                      <div className="bg-primary/10 p-2">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">#{booking.booking_id.slice(-8)}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatDate(booking.start_date)} - {formatDate(booking.end_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`px-2 py-1 text-xs font-medium ${getStatusColor(booking.status)}`}>
                        {getStatusLabel(booking.status)}
                      </span>
                      <span className="font-mono font-bold">{formatCurrency(booking.total_price)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
